const API_BASE = '';

const getAuthHeaders = () => {
  const token = localStorage.getItem('accessToken');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

const handleResponse = async (response) => {
  // Read body as text first (works for JSON and non-JSON like Cloudflare error pages)
  let bodyText = '';
  try { bodyText = await response.text(); } catch (_) {}

  let data = null;
  if (bodyText) {
    try { data = JSON.parse(bodyText); } catch (_) {}
  }

  if (!response.ok) {
    let err;
    if (response.status >= 500) {
      err = new Error('Server temporarily unavailable. Please try again.');
    } else {
      const detail = (data && typeof data === 'object') ? data.detail : '';
      err = new Error(detail || 'Request failed');
    }
    err.status = response.status;
    throw err;
  }

  // Return parsed data (arrays, objects, etc.) or fallback
  return data !== null ? data : { success: true };
};

// Auth API
export const authAPI = {
  login: (email, password) =>
    fetch(`${API_BASE}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    }).then(handleResponse),

  verify2FA: (twoFactorToken, code) =>
    fetch(`${API_BASE}/api/auth/2fa/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ twoFactorToken, code })
    }).then(handleResponse),

  setup2FA: () =>
    fetch(`${API_BASE}/api/auth/2fa/setup`, {
      method: 'POST',
      headers: getAuthHeaders()
    }).then(handleResponse),

  verifySetup2FA: (code) =>
    fetch(`${API_BASE}/api/auth/2fa/verify-setup`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ code })
    }).then(handleResponse),

  disable2FA: (code) =>
    fetch(`${API_BASE}/api/auth/2fa/disable`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ code })
    }).then(handleResponse),

  changePassword: (currentPassword, newPassword, twoFactorCode) =>
    fetch(`${API_BASE}/api/auth/change-password`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ currentPassword, newPassword, twoFactorCode: twoFactorCode || undefined })
    }).then(handleResponse),

  getMe: () =>
    fetch(`${API_BASE}/api/auth/me`, {
      headers: getAuthHeaders()
    }).then(handleResponse),

  logout: () =>
    fetch(`${API_BASE}/api/auth/logout`, {
      method: 'POST',
      headers: getAuthHeaders()
    }).then(handleResponse),

  requestPasswordReset: (email) =>
    fetch(`${API_BASE}/api/auth/request-password-reset`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email })
    }).then(handleResponse),

  resetPassword: (token, newPassword) =>
    fetch(`${API_BASE}/api/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, newPassword })
    }).then(handleResponse)
};

// Users API
export const usersAPI = {
  list: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return fetch(`${API_BASE}/api/users?${query}`, {
      headers: getAuthHeaders()
    }).then(handleResponse);
  },

  create: (userData) =>
    fetch(`${API_BASE}/api/auth/register`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(userData)
    }).then(handleResponse),

  update: (userId, userData) =>
    fetch(`${API_BASE}/api/users/${userId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(userData)
    }).then(handleResponse),

  delete: (userId) =>
    fetch(`${API_BASE}/api/users/${userId}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    }).then(handleResponse),

  reset2FA: (userId, reason) =>
    fetch(`${API_BASE}/api/admin/users/${userId}/reset-2fa`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ reason })
    }).then(handleResponse),

  revokeSessions: (userId, reason = '') =>
    fetch(`${API_BASE}/api/admin/users/${userId}/revoke-sessions`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ reason })
    }).then(handleResponse),

  getLoginAbuseAlerts: () =>
    fetch(`${API_BASE}/api/admin/login-abuse-alerts`, {
      headers: getAuthHeaders()
    }).then(handleResponse)
};

// Counterparties API
export const counterpartiesAPI = {
  list: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return fetch(`${API_BASE}/api/counterparties?${query}`, {
      headers: getAuthHeaders()
    }).then(handleResponse);
  },

  get: (id) =>
    fetch(`${API_BASE}/api/counterparties/${id}`, {
      headers: getAuthHeaders()
    }).then(handleResponse),

  getMembers: (id) =>
    fetch(`${API_BASE}/api/counterparties/${id}/members`, {
      headers: getAuthHeaders()
    }).then(handleResponse),

  create: (data) =>
    fetch(`${API_BASE}/api/counterparties`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    }).then(handleResponse),

  update: (id, data) =>
    fetch(`${API_BASE}/api/counterparties/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    }).then(handleResponse),

  delete: (id) =>
    fetch(`${API_BASE}/api/counterparties/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    }).then(handleResponse),

  addNote: (id, note) =>
    fetch(`${API_BASE}/api/counterparties/${id}/notes`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(note)
    }).then(handleResponse),

  downloadTemplate: () =>
    fetch(`${API_BASE}/api/counterparties/import/template`, {
      headers: getAuthHeaders()
    }).then(r => r.blob()),

  validateImport: (file, mode, columnMapping) => {
    const form = new FormData();
    form.append('file', file);
    form.append('mode', mode);
    if (columnMapping) form.append('column_mapping', JSON.stringify(columnMapping));
    const headers = getAuthHeaders();
    delete headers['Content-Type'];
    return fetch(`${API_BASE}/api/counterparties/import/validate`, {
      method: 'POST',
      headers,
      body: form
    }).then(handleResponse);
  },

  executeImport: (file, mode, columnMapping) => {
    const form = new FormData();
    form.append('file', file);
    form.append('mode', mode);
    if (columnMapping) form.append('column_mapping', JSON.stringify(columnMapping));
    const headers = getAuthHeaders();
    delete headers['Content-Type'];
    return fetch(`${API_BASE}/api/counterparties/import/execute`, {
      method: 'POST',
      headers,
      body: form
    }).then(handleResponse);
  },

  getImportJobStatus: (jobId) =>
    fetch(`${API_BASE}/api/import-jobs/${jobId}`, {
      headers: getAuthHeaders()
    }).then(handleResponse),

  bulk: (ids, action, value) =>
    fetch(`${API_BASE}/api/counterparties/bulk`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids, action, value })
    }).then(handleResponse),
};

// Deals API
export const dealsAPI = {
  list: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return fetch(`${API_BASE}/api/deals?${query}`, {
      headers: getAuthHeaders()
    }).then(handleResponse);
  },

  get: (id) =>
    fetch(`${API_BASE}/api/deals/${id}`, {
      headers: getAuthHeaders()
    }).then(handleResponse),

  create: (data) =>
    fetch(`${API_BASE}/api/deals`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    }).then(handleResponse),

  update: (id, data) =>
    fetch(`${API_BASE}/api/deals/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    }).then(handleResponse),

  delete: (id, reason = '') =>
    fetch(`${API_BASE}/api/deals/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
      body: JSON.stringify({ reason: reason || '' })
    }).then(handleResponse),

  duplicate: (id, name = '') =>
    fetch(`${API_BASE}/api/deals/${id}/duplicate`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(name ? { name } : {})
    }).then(handleResponse),

  restore: (id) =>
    fetch(`${API_BASE}/api/deals/${id}/restore`, {
      method: 'POST',
      headers: getAuthHeaders()
    }).then(handleResponse),

  listTrash: () =>
    fetch(`${API_BASE}/api/admin/deals/trash`, {
      headers: getAuthHeaders()
    }).then(handleResponse),

  getMyInterest: (dealId) =>
    fetch(`${API_BASE}/api/deals/${dealId}/participants/me/interest`, {
      headers: getAuthHeaders()
    }).then(handleResponse),

  setMyInterest: (dealId, response, opts = {}) =>
    fetch(`${API_BASE}/api/deals/${dealId}/participants/me/interest`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      // Phase 88 — accept structured payload. opts may carry { subtype, reason, note }.
      // Backend treats subtype/reason as optional (legacy compat) but the UI now
      // always sends a subtype on interested and a reason on not_interested.
      body: JSON.stringify({ response, ...opts })
    }).then(handleResponse),

  // Phase 88 — Internal-only aggregated response intelligence for a deal.
  getDealResponseSummary: (dealId) =>
    fetch(`${API_BASE}/api/admin/deals/${dealId}/response-summary`, {
      headers: getAuthHeaders()
    }).then(handleResponse),

  listMyDispatchedPackages: (dealId) =>
    fetch(`${API_BASE}/api/deals/${dealId}/dispatch-packages-mine`, {
      headers: getAuthHeaders()
    }).then(handleResponse),

  downloadSignedCopy: (dealId, pkgId) =>
    fetch(`${API_BASE}/api/deals/${dealId}/dispatch-packages/${pkgId}/signed-copy/download`, {
      headers: getAuthHeaders()
    }),

  invite: (dealId, counterpartyId, accessLevel = 'teaser') =>
    fetch(`${API_BASE}/api/deals/${dealId}/invite?counterparty_id=${counterpartyId}&access_level=${accessLevel}`, {
      method: 'POST',
      headers: getAuthHeaders()
    }).then(handleResponse),

  revokeAccess: (dealId, counterpartyId) =>
    fetch(`${API_BASE}/api/deals/${dealId}/invite/${counterpartyId}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    }).then(handleResponse),

  acceptNDA: (dealId) =>
    fetch(`${API_BASE}/api/deals/${dealId}/accept-nda`, {
      method: 'POST',
      headers: getAuthHeaders()
    }).then(handleResponse),

  getDocuments: (dealId) =>
    fetch(`${API_BASE}/api/deals/${dealId}/documents`, {
      headers: getAuthHeaders()
    }).then(handleResponse),

  setBrokerCommission: (dealId, data) =>
    fetch(`${API_BASE}/api/deals/${dealId}/broker-commission`, {
      method: 'PUT',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }).then(handleResponse),

  // Commission Proposals (P4)
  createCommissionProposal: (dealId, data) =>
    fetch(`${API_BASE}/api/deals/${dealId}/commission-proposals`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }).then(handleResponse),

  listCommissionProposals: (dealId) =>
    fetch(`${API_BASE}/api/deals/${dealId}/commission-proposals`, {
      headers: getAuthHeaders(),
    }).then(handleResponse),

  getCommissionProposal: (dealId, proposalId) =>
    fetch(`${API_BASE}/api/deals/${dealId}/commission-proposals/${proposalId}`, {
      headers: getAuthHeaders(),
    }).then(handleResponse),

  updateCommissionProposal: (dealId, proposalId, data) =>
    fetch(`${API_BASE}/api/deals/${dealId}/commission-proposals/${proposalId}`, {
      method: 'PUT',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }).then(handleResponse),

  submitCommissionProposal: (dealId, proposalId) =>
    fetch(`${API_BASE}/api/deals/${dealId}/commission-proposals/${proposalId}/submit`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    }).then(handleResponse),

  approveCommissionProposal: (dealId, proposalId, data) =>
    fetch(`${API_BASE}/api/deals/${dealId}/commission-proposals/${proposalId}/approve`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data || {}),
    }).then(handleResponse),

  requestRevisionCommissionProposal: (dealId, proposalId, data) =>
    fetch(`${API_BASE}/api/deals/${dealId}/commission-proposals/${proposalId}/request-revision`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data || {}),
    }).then(handleResponse),

  rejectCommissionProposal: (dealId, proposalId, data) =>
    fetch(`${API_BASE}/api/deals/${dealId}/commission-proposals/${proposalId}/reject`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data || {}),
    }).then(handleResponse),

  withdrawCommissionProposal: (dealId, proposalId) =>
    fetch(`${API_BASE}/api/deals/${dealId}/commission-proposals/${proposalId}/withdraw`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    }).then(handleResponse),

  uploadDocument: (dealId, formData) =>
    fetch(`${API_BASE}/api/deals/${dealId}/documents`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
      },
      body: formData
    }).then(handleResponse),

  // ── Large File Uploads (direct-to-S3 presigned PUT) ──
  // Use these for files > ~20 MB. Presigns a URL, lets the browser PUT straight
  // to S3, then finalizes the document record. Bypasses FastAPI body size limits
  // and proxy timeouts entirely.
  presignDocumentUpload: async (dealId, payload) => {
    const resp = await fetch(`${API_BASE}/api/deals/${dealId}/documents/presign-upload`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await resp.json().catch(() => ({}));
    if (!resp.ok) {
      const err = new Error(data.detail || `Presign failed (${resp.status})`);
      err.status = resp.status;
      err.body = data;
      throw err;
    }
    return data;
  },

  finalizeDocumentUpload: async (dealId, uploadId) => {
    const resp = await fetch(`${API_BASE}/api/deals/${dealId}/documents/finalize-upload`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ uploadId }),
    });
    const data = await resp.json().catch(() => ({}));
    if (!resp.ok) {
      const err = new Error(data.detail || `Finalize failed (${resp.status})`);
      err.status = resp.status;
      err.body = data;
      throw err;
    }
    return data;
  },

  presignVersionUpload: async (docId, payload) => {
    const resp = await fetch(`${API_BASE}/api/documents/${docId}/presign-version`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await resp.json().catch(() => ({}));
    if (!resp.ok) {
      const err = new Error(data.detail || `Presign version failed (${resp.status})`);
      err.status = resp.status;
      err.body = data;
      throw err;
    }
    return data;
  },

  finalizeVersionUpload: async (docId, uploadId) => {
    const resp = await fetch(`${API_BASE}/api/documents/${docId}/finalize-version`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ uploadId }),
    });
    const data = await resp.json().catch(() => ({}));
    if (!resp.ok) {
      const err = new Error(data.detail || `Finalize version failed (${resp.status})`);
      err.status = resp.status;
      err.body = data;
      throw err;
    }
    return data;
  },

  getQuestions: (dealId) =>
    fetch(`${API_BASE}/api/deals/${dealId}/questions`, {
      headers: getAuthHeaders()
    }).then(handleResponse),

  getActivity: (dealId, params = {}) => {
    const query = new URLSearchParams();
    if (params.from) query.set('from', params.from);
    if (params.to) query.set('to', params.to);
    if (params.page) query.set('page', params.page);
    if (params.pageSize) query.set('pageSize', params.pageSize);
    return fetch(`${API_BASE}/api/deals/${dealId}/activity?${query}`, {
      headers: getAuthHeaders()
    }).then(handleResponse);
  },

  submitQuestion: (dealId, data) =>
    fetch(`${API_BASE}/api/deals/${dealId}/questions`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    }).then(handleResponse),

  previewDocument: async (docId) => {
    const token = localStorage.getItem('accessToken');
    const resp = await fetch(`${API_BASE}/api/documents/${docId}/preview`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!resp.ok) throw new Error('Failed to load document');
    return resp.blob();
  },

  previewDocumentRaw: (docId) => {
    const token = localStorage.getItem('accessToken');
    return fetch(`${API_BASE}/api/documents/${docId}/preview`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
  },

  previewDocumentMeta: async (docId) => {
    const token = localStorage.getItem('accessToken');
    const resp = await fetch(`${API_BASE}/api/documents/${docId}/preview-meta`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!resp.ok) {
      const detail = await resp.json().catch(() => ({}));
      const err = new Error(detail?.detail || 'Failed to load preview metadata');
      err.status = resp.status;
      throw err;
    }
    return resp.json();
  },

  downloadDocument: (docId) => {
    const token = localStorage.getItem('accessToken');
    return fetch(`${API_BASE}/api/documents/${docId}/download`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
  },

  updateDocumentPermissions: (docId, externalAccess) =>
    fetch(`${API_BASE}/api/documents/${docId}/permissions`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ externalAccess })
    }).then(handleResponse),

  uploadNewVersion: (docId, formData) =>
    fetch(`${API_BASE}/api/documents/${docId}/versions`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
      },
      body: formData
    }).then(handleResponse),

  listVersions: (docId) =>
    fetch(`${API_BASE}/api/documents/${docId}/versions`, {
      headers: getAuthHeaders()
    }).then(handleResponse),

  previewVersion: async (docId, version) => {
    const token = localStorage.getItem('accessToken');
    const resp = await fetch(`${API_BASE}/api/documents/${docId}/versions/${version}/preview`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!resp.ok) throw new Error('Failed to load version');
    return resp.blob();
  },

  downloadVersion: (docId, version) => {
    const token = localStorage.getItem('accessToken');
    return fetch(`${API_BASE}/api/documents/${docId}/versions/${version}/download`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
  },

  revertToVersion: (docId, version) =>
    fetch(`${API_BASE}/api/documents/${docId}/versions/${version}/revert`, {
      method: 'POST',
      headers: getAuthHeaders()
    }).then(handleResponse),

  // ── Folder Upload & Browsing ──

  folderUpload: (dealId, formData) =>
    fetch(`${API_BASE}/api/deals/${dealId}/documents/folder-upload`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` },
      body: formData,
    }).then(handleResponse),

  folderSubmit: (dealId, formData) =>
    fetch(`${API_BASE}/api/deals/${dealId}/documents/folder-submit`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` },
      body: formData,
    }).then(handleResponse),

  getFolderTree: (dealId) =>
    fetch(`${API_BASE}/api/deals/${dealId}/documents/folders`, {
      headers: getAuthHeaders(),
    }).then(handleResponse),

  getSubmissions: (dealId) =>
    fetch(`${API_BASE}/api/deals/${dealId}/documents/submissions`, {
      headers: getAuthHeaders(),
    }).then(handleResponse),

  reviewSubmission: (dealId, batchId, body) =>
    fetch(`${API_BASE}/api/deals/${dealId}/documents/submissions/${batchId}/review`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    }).then(handleResponse),

  bulkUpdatePermissions: (dealId, body) =>
    fetch(`${API_BASE}/api/deals/${dealId}/documents/bulk-permissions`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    }).then(handleResponse),

  getMySubmissions: (dealId) =>
    fetch(`${API_BASE}/api/deals/${dealId}/my-submissions`, {
      headers: getAuthHeaders(),
    }).then(handleResponse),

  getStatusHistory: (dealId) =>
    fetch(`${API_BASE}/api/deals/${dealId}/status-history`, {
      headers: getAuthHeaders(),
    }).then(handleResponse),

  getPipeline: () =>
    fetch(`${API_BASE}/api/admin/pipeline`, {
      headers: getAuthHeaders(),
    }).then(handleResponse),

  // Phase 69 — Distribution-intelligence per-deal response counts.
  // Internal-only. Returns { summaries: { [dealId]: { interested, notInterested,
  //   noResponse, totalExternal, recentlyChanged } }, count }
  getDistributionSummary: () =>
    fetch(`${API_BASE}/api/admin/deals/distribution-summary`, {
      headers: getAuthHeaders(),
    }).then(handleResponse),
};

// Bids API
export const bidsAPI = {
  listForDeal: (dealId) =>
    fetch(`${API_BASE}/api/deals/${dealId}/bids`, {
      headers: getAuthHeaders()
    }).then(handleResponse),

  submit: (data) =>
    fetch(`${API_BASE}/api/bids`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    }).then(handleResponse),

  update: (bidId, data) =>
    fetch(`${API_BASE}/api/bids/${bidId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    }).then(handleResponse)
};

// Questions API
export const questionsAPI = {
  answer: (questionId, data) =>
    fetch(`${API_BASE}/api/questions/${questionId}/answer`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    }).then(handleResponse)
};

// Notifications API
export const notificationsAPI = {
  list: (unreadOnly = false) =>
    fetch(`${API_BASE}/api/notifications?unread_only=${unreadOnly}`, {
      headers: getAuthHeaders()
    }).then(handleResponse),

  markRead: (id) =>
    fetch(`${API_BASE}/api/notifications/${id}/read`, {
      method: 'PUT',
      headers: getAuthHeaders()
    }).then(handleResponse)
};

// User Preferences API
export const preferencesAPI = {
  get: () =>
    fetch(`${API_BASE}/api/users/me/preferences`, {
      headers: getAuthHeaders()
    }).then(handleResponse),
  update: (data) =>
    fetch(`${API_BASE}/api/users/me/preferences`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    }).then(handleResponse),
};

// Activity API
export const activityAPI = {
  list: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return fetch(`${API_BASE}/api/activity?${query}`, {
      headers: getAuthHeaders()
    }).then(handleResponse);
  },
  summary: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return fetch(`${API_BASE}/api/activity/summary?${query}`, {
      headers: getAuthHeaders()
    }).then(handleResponse);
  },
  exportCsv: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return fetch(`${API_BASE}/api/activity/export?${query}`, {
      headers: getAuthHeaders()
    }).then(res => {
      if (!res.ok) throw new Error('Export failed');
      return res.blob();
    });
  },
  users: () => {
    return fetch(`${API_BASE}/api/users`, {
      headers: getAuthHeaders()
    }).then(handleResponse);
  },
  deals: () => {
    return fetch(`${API_BASE}/api/deals`, {
      headers: getAuthHeaders()
    }).then(handleResponse);
  },
  // Scheduled audit reports
  listSchedules: () =>
    fetch(`${API_BASE}/api/audit-schedules`, { headers: getAuthHeaders() }).then(handleResponse),
  createSchedule: (data) =>
    fetch(`${API_BASE}/api/audit-schedules`, {
      method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data)
    }).then(handleResponse),
  updateSchedule: (id, data) =>
    fetch(`${API_BASE}/api/audit-schedules/${id}`, {
      method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(data)
    }).then(handleResponse),
  deleteSchedule: (id) =>
    fetch(`${API_BASE}/api/audit-schedules/${id}`, {
      method: 'DELETE', headers: getAuthHeaders()
    }).then(handleResponse),
  sendScheduleNow: (id) =>
    fetch(`${API_BASE}/api/audit-schedules/${id}/send-now`, {
      method: 'POST', headers: getAuthHeaders()
    }).then(handleResponse),
  getScheduleLogs: (id) =>
    fetch(`${API_BASE}/api/audit-schedules/${id}/logs`, {
      headers: getAuthHeaders()
    }).then(handleResponse),
};

// Tasks API
export const tasksAPI = {
  list: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return fetch(`${API_BASE}/api/tasks${qs ? '?' + qs : ''}`, { headers: getAuthHeaders() }).then(handleResponse);
  },
  dashboard: () =>
    fetch(`${API_BASE}/api/tasks/dashboard`, { headers: getAuthHeaders() }).then(handleResponse),
  get: (id) =>
    fetch(`${API_BASE}/api/tasks/${id}`, { headers: getAuthHeaders() }).then(handleResponse),
  create: (data) =>
    fetch(`${API_BASE}/api/tasks`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  update: (id, data) =>
    fetch(`${API_BASE}/api/tasks/${id}`, { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  delete: (id) =>
    fetch(`${API_BASE}/api/tasks/${id}`, { method: 'DELETE', headers: getAuthHeaders() }).then(handleResponse),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () =>
    fetch(`${API_BASE}/api/dashboard`, {
      headers: getAuthHeaders()
    }).then(handleResponse)
};

export const importHistoryAPI = {
  list: (params = {}) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v) query.set(k, v); });
    return fetch(`${API_BASE}/api/import-history?${query}`, {
      headers: getAuthHeaders()
    }).then(handleResponse);
  },
  getDetail: (id) =>
    fetch(`${API_BASE}/api/import-history/${id}`, {
      headers: getAuthHeaders()
    }).then(handleResponse),
};

export const importPresetsAPI = {
  list: (importType) => {
    const query = importType ? `?import_type=${importType}` : '';
    return fetch(`${API_BASE}/api/import-presets${query}`, {
      headers: getAuthHeaders()
    }).then(handleResponse);
  },
  create: (data) =>
    fetch(`${API_BASE}/api/import-presets`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }).then(handleResponse),
  update: (id, data) =>
    fetch(`${API_BASE}/api/import-presets/${id}`, {
      method: 'PUT',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }).then(handleResponse),
  delete: (id) =>
    fetch(`${API_BASE}/api/import-presets/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    }).then(handleResponse),
  markUsed: (id) =>
    fetch(`${API_BASE}/api/import-presets/${id}`, {
      method: 'PUT',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ markUsed: true }),
    }).then(handleResponse),
};

// Agreements API (Phase 1b — deal-scoped agreement gating)
export const agreementsAPI = {
  getStatus: (dealId) =>
    fetch(`${API_BASE}/api/deals/${dealId}/agreement-status`, {
      headers: getAuthHeaders()
    }).then(handleResponse),

  accept: (dealId, templateId) =>
    fetch(`${API_BASE}/api/deals/${dealId}/accept-agreement`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ templateId })
    }).then(handleResponse),
};

// Deal Participants API (Phase 1d — admin controls)
export const participantsAPI = {
  list: (dealId) =>
    fetch(`${API_BASE}/api/deals/${dealId}/participants`, {
      headers: getAuthHeaders()
    }).then(handleResponse),

  add: (dealId, userId, opts = {}) =>
    fetch(`${API_BASE}/api/deals/${dealId}/participants`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ userId, ...opts })
    }).then(handleResponse),

  update: (dealId, userId, data) =>
    fetch(`${API_BASE}/api/deals/${dealId}/participants/${userId}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    }).then(handleResponse),

  remove: (dealId, userId) =>
    fetch(`${API_BASE}/api/deals/${dealId}/participants/${userId}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    }).then(handleResponse),
};

// Deal Messaging API (Phase 1c — deal-scoped threads & messages)
export const messagingAPI = {
  listThreads: (dealId) =>
    fetch(`${API_BASE}/api/deals/${dealId}/threads`, {
      headers: getAuthHeaders()
    }).then(handleResponse),

  getConversations: (dealId) =>
    fetch(`${API_BASE}/api/deals/${dealId}/conversations`, {
      headers: getAuthHeaders()
    }).then(handleResponse),

  createThread: (dealId, data) =>
    fetch(`${API_BASE}/api/deals/${dealId}/threads`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    }).then(handleResponse),

  listMessages: (dealId, threadId, skip = 0, limit = 50, q = '') =>
    fetch(`${API_BASE}/api/deals/${dealId}/threads/${threadId}/messages?skip=${skip}&limit=${limit}${q ? `&q=${encodeURIComponent(q)}` : ''}`, {
      headers: getAuthHeaders()
    }).then(handleResponse),

  sendMessage: (dealId, threadId, content, files = []) => {
    if (files.length > 0) {
      const formData = new FormData();
      formData.append('content', content || '');
      files.forEach(f => formData.append('files', f));
      const token = localStorage.getItem('accessToken');
      return fetch(`${API_BASE}/api/deals/${dealId}/threads/${threadId}/messages`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      }).then(handleResponse);
    }
    return fetch(`${API_BASE}/api/deals/${dealId}/threads/${threadId}/messages`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ content })
    }).then(handleResponse);
  },

  downloadAttachment: (messageId, attachmentIdx) => {
    const token = localStorage.getItem('accessToken');
    return fetch(`${API_BASE}/api/thread-attachments/${messageId}/${attachmentIdx}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
  },

  markRead: (dealId, threadId) =>
    fetch(`${API_BASE}/api/deals/${dealId}/conversations/${threadId}/read`, {
      method: 'POST',
      headers: getAuthHeaders()
    }).then(handleResponse),

  addThreadParticipant: (dealId, threadId, userId) =>
    fetch(`${API_BASE}/api/deals/${dealId}/threads/${threadId}/participants`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ userId })
    }).then(handleResponse),

  removeThreadParticipant: (dealId, threadId, userId) =>
    fetch(`${API_BASE}/api/deals/${dealId}/threads/${threadId}/participants/${userId}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    }).then(handleResponse),

  toggleMute: (dealId, threadId) =>
    fetch(`${API_BASE}/api/deals/${dealId}/threads/${threadId}/mute`, {
      method: 'PUT',
      headers: getAuthHeaders()
    }).then(handleResponse),
};

export const commissionProposalsAPI = {
  getPendingCount: () =>
    fetch(`${API_BASE}/api/commission-proposals/pending-count`, {
      headers: getAuthHeaders()
    }).then(handleResponse),
  listPending: () =>
    fetch(`${API_BASE}/api/commission-proposals/pending`, {
      headers: getAuthHeaders()
    }).then(handleResponse),
};

export const adminCounterpartiesAPI = {
  getPendingReviewCount: () =>
    fetch(`${API_BASE}/api/admin/pending-review/count`, {
      headers: getAuthHeaders()
    }).then(handleResponse),
  listPendingReview: () =>
    fetch(`${API_BASE}/api/admin/counterparties/pending-review`, {
      headers: getAuthHeaders()
    }).then(handleResponse),
  approve: (id) =>
    fetch(`${API_BASE}/api/admin/counterparties/${id}/approve`, {
      method: 'POST', headers: getAuthHeaders()
    }).then(handleResponse),
  linkExisting: (pendingId, existingId) =>
    fetch(`${API_BASE}/api/admin/counterparties/${pendingId}/link-existing/${existingId}`, {
      method: 'POST', headers: getAuthHeaders()
    }).then(handleResponse),
  merge: (sourceId, targetId) =>
    fetch(`${API_BASE}/api/admin/counterparties/merge`, {
      method: 'POST', headers: getAuthHeaders(),
      body: JSON.stringify({ sourceId, targetId })
    }).then(handleResponse),
};

export const documentsAdminAPI = {
  getPendingSubmissionsSummary: () =>
    fetch(`${API_BASE}/api/documents/pending-submissions-summary`, {
      headers: getAuthHeaders()
    }).then(handleResponse),
  renameFolder: (dealId, oldPath, newName) =>
    fetch(`${API_BASE}/api/deals/${dealId}/documents/folder-rename`, {
      method: 'PATCH',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ oldPath, newName }),
    }).then(handleResponse),
  moveDocuments: (dealId, data) =>
    fetch(`${API_BASE}/api/deals/${dealId}/documents/move`, {
      method: 'PATCH',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }).then(handleResponse),
  deleteFolder: (dealId, folderPath) =>
    fetch(`${API_BASE}/api/deals/${dealId}/documents/folder`, {
      method: 'DELETE',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ folderPath }),
    }).then(handleResponse),
  getFolderMetadata: (dealId) =>
    fetch(`${API_BASE}/api/deals/${dealId}/folder-metadata`, { headers: getAuthHeaders() }).then(handleResponse),
  setFolderMetadata: (dealId, folderPath, notes, label) =>
    fetch(`${API_BASE}/api/deals/${dealId}/folder-metadata`, {
      method: 'PUT',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ folderPath, notes, label }),
    }).then(handleResponse),
  getFolderPermissions: (dealId) =>
    fetch(`${API_BASE}/api/deals/${dealId}/folder-permissions`, { headers: getAuthHeaders() }).then(handleResponse),
  setFolderPermission: (dealId, folderPath, accessLevel, externalAccess) =>
    fetch(`${API_BASE}/api/deals/${dealId}/folder-permissions`, {
      method: 'PUT',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ folderPath, accessLevel, externalAccess }),
    }).then(handleResponse),
  deleteFolderPermission: (dealId, folderPath) =>
    fetch(`${API_BASE}/api/deals/${dealId}/folder-permissions`, {
      method: 'DELETE',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ folderPath }),
    }).then(handleResponse),
  getDocumentAnalytics: () =>
    fetch(`${API_BASE}/api/admin/document-analytics`, { headers: getAuthHeaders() }).then(handleResponse),
  applyDefaultFolderTemplate: (dealId) =>
    fetch(`${API_BASE}/api/deals/${dealId}/folder-templates/apply`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
    }).then(handleResponse),
  prepareDataRoom: (dealId) =>
    fetch(`${API_BASE}/api/deals/${dealId}/prepare-data-room`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
    }).then(handleResponse),
  getDataRoomStatus: (dealId) =>
    fetch(`${API_BASE}/api/deals/${dealId}/data-room-status`, {
      headers: getAuthHeaders(),
    }).then(handleResponse),
  getActiveAccess: (dealId) =>
    fetch(`${API_BASE}/api/deals/${dealId}/active-access`, {
      headers: getAuthHeaders(),
    }).then(handleResponse),
  listMyAgreementAcceptances: () =>
    fetch(`${API_BASE}/api/agreement-acceptances/mine`, {
      headers: getAuthHeaders(),
    }).then(handleResponse),
  downloadAgreementPdf: (acceptanceId) =>
    `${API_BASE}/api/agreement-acceptances/${acceptanceId}/pdf`,
  renameDocument: (docId, name, category) =>
    fetch(`${API_BASE}/api/documents/${docId}/rename`, {
      method: 'PATCH',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, ...(category ? { category } : {}) }),
    }).then(handleResponse),
};

/* ── Document Requests API ── */
export const docRequestsAPI = {
  list: (dealId) =>
    fetch(`${API_BASE}/api/deals/${dealId}/doc-requests`, { headers: getAuthHeaders() }).then(handleResponse),
  create: (dealId, data) =>
    fetch(`${API_BASE}/api/deals/${dealId}/doc-requests`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }).then(handleResponse),
  update: (dealId, reqId, data) =>
    fetch(`${API_BASE}/api/deals/${dealId}/doc-requests/${reqId}`, {
      method: 'PUT',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }).then(handleResponse),
  fulfill: (dealId, reqId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return fetch(`${API_BASE}/api/deals/${dealId}/doc-requests/${reqId}/fulfill`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
      body: fd,
    }).then(handleResponse);
  },
  review: (dealId, reqId, decision, reviewNotes) =>
    fetch(`${API_BASE}/api/deals/${dealId}/doc-requests/${reqId}/review`, {
      method: 'PUT',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ decision, reviewNotes }),
    }).then(handleResponse),
  delete: (dealId, reqId) =>
    fetch(`${API_BASE}/api/deals/${dealId}/doc-requests/${reqId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    }).then(handleResponse),
  close: (dealId, reqId, closeReason) =>
    fetch(`${API_BASE}/api/deals/${dealId}/doc-requests/${reqId}/close`, {
      method: 'PUT',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ closeReason }),
    }).then(handleResponse),
  reopen: (dealId, reqId) =>
    fetch(`${API_BASE}/api/deals/${dealId}/doc-requests/${reqId}/reopen`, {
      method: 'PUT',
      headers: getAuthHeaders(),
    }).then(handleResponse),
};

/* ── Workflow Signals API ── */
export const workflowSignalsAPI = {
  list: (dealId) =>
    fetch(`${API_BASE}/api/deals/${dealId}/workflow-signals`, { headers: getAuthHeaders() }).then(handleResponse),
  dismiss: (dealId, signalKey) =>
    fetch(`${API_BASE}/api/deals/${dealId}/workflow-signals/dismiss`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ signalKey }),
    }).then(handleResponse),
};

export const counterpartyViewsAPI = {
  list: () =>
    fetch(`${API_BASE}/api/counterparty-views`, {
      headers: getAuthHeaders()
    }).then(handleResponse),
  create: (data) =>
    fetch(`${API_BASE}/api/counterparty-views`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }).then(handleResponse),
  update: (id, data) =>
    fetch(`${API_BASE}/api/counterparty-views/${id}`, {
      method: 'PUT',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }).then(handleResponse),
  delete: (id) =>
    fetch(`${API_BASE}/api/counterparty-views/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    }).then(handleResponse),
};

export default {
  auth: authAPI,
  users: usersAPI,
  counterparties: counterpartiesAPI,
  deals: dealsAPI,
  bids: bidsAPI,
  questions: questionsAPI,
  notifications: notificationsAPI,
  preferences: preferencesAPI,
  activity: activityAPI,
  dashboard: dashboardAPI,
  tasks: tasksAPI,
  importHistory: importHistoryAPI,
  agreements: agreementsAPI,
  participants: participantsAPI,
  messaging: messagingAPI,
};


// ─── Phase 73: Saved Views / Ops Shortcuts (internal-only, per-user) ───
export const savedViewsAPI = {
  // page is one of "deals" | "pipeline" | undefined (=all pages)
  list: (page) => {
    const url = `${API_BASE}/api/saved-views${page ? `?page=${encodeURIComponent(page)}` : ''}`;
    return fetch(url, { headers: getAuthHeaders() }).then(handleResponse);
  },

  create: (payload) =>
    fetch(`${API_BASE}/api/saved-views`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    }).then(handleResponse),

  update: (id, patch) =>
    fetch(`${API_BASE}/api/saved-views/${id}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(patch),
    }).then(handleResponse),

  delete: (id) =>
    fetch(`${API_BASE}/api/saved-views/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    }).then(handleResponse),

  // Phase 77: default / landing view.
  setDefault: (id) =>
    fetch(`${API_BASE}/api/saved-views/${id}/set-default`, {
      method: 'POST',
      headers: getAuthHeaders(),
    }).then(handleResponse),

  clearDefault: (id) =>
    fetch(`${API_BASE}/api/saved-views/${id}/clear-default`, {
      method: 'POST',
      headers: getAuthHeaders(),
    }).then(handleResponse),

  // Phase 80: shared / team view toggle.
  toggleShare: (id, shared) =>
    fetch(`${API_BASE}/api/saved-views/${id}/share`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ shared }),
    }).then(handleResponse),

  // Phase 82: clone a (private or shared) view into my private list.
  clone: (id, name) =>
    fetch(`${API_BASE}/api/saved-views/${id}/clone`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(name ? { name } : {}),
    }).then(handleResponse),
};

// ─── Phase 76: Distribution action layer (nudge lenders) ────────────────
export const distributionActionsAPI = {
  // body: { preview?: boolean, force?: boolean, participantIds?: string[] }
  nudgeNoResponse: (dealId, body = {}) =>
    fetch(`${API_BASE}/api/admin/deals/${dealId}/nudge-no-response`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    }).then(handleResponse),
};

// ─── Phase 70: Re-Index Queue API (internal-only) ─────────────────
export const reindexAPI = {
  list: (params = {}) => {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.url) qs.set('url', params.url);
    const url = `${API_BASE}/api/admin/reindex${qs.toString() ? '?' + qs.toString() : ''}`;
    return fetch(url, { headers: getAuthHeaders() }).then(handleResponse);
  },
  resolve: (id, payload) =>
    fetch(`${API_BASE}/api/admin/reindex/${id}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    }).then(handleResponse),
};


// ─── Invite & NCNDA API ──────────────────────────────────────────
export const inviteAPI = {
  validate: (token) =>
    fetch(`${API_BASE}/api/invites/validate/${token}`).then(handleResponse),
  activate: (token, data) =>
    fetch(`${API_BASE}/api/invites/activate/${token}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data)
    }).then(handleResponse),
  create: (data) =>
    fetch(`${API_BASE}/api/invites`, {
      method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data)
    }).then(handleResponse),
  resend: (inviteId) =>
    fetch(`${API_BASE}/api/invites/${inviteId}/resend`, {
      method: 'POST', headers: getAuthHeaders()
    }).then(handleResponse),
  bulkResend: (inviteIds) =>
    fetch(`${API_BASE}/api/invites/bulk-resend`, {
      method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ inviteIds })
    }).then(handleResponse),
  listForDeal: (dealId) =>
    fetch(`${API_BASE}/api/invites/deal/${dealId}`, { headers: getAuthHeaders() }).then(handleResponse),
  dealGuardrails: (dealId) =>
    fetch(`${API_BASE}/api/invites/deal/${dealId}/guardrails`, { headers: getAuthHeaders() }).then(handleResponse),
  analytics: (period = '90d', page = 1, pageSize = 25) =>
    fetch(`${API_BASE}/api/invites/analytics?period=${period}&page=${page}&page_size=${pageSize}`, { headers: getAuthHeaders() }).then(handleResponse),
  bulkInvite: (data) =>
    fetch(`${API_BASE}/api/invites/bulk`, {
      method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data)
    }).then(handleResponse),
  listPending: () =>
    fetch(`${API_BASE}/api/invites/pending`, { headers: getAuthHeaders() }).then(handleResponse),
  revoke: (inviteId) =>
    fetch(`${API_BASE}/api/invites/${inviteId}/revoke`, {
      method: 'POST', headers: getAuthHeaders()
    }).then(handleResponse),
};

export const matchesAPI = {
  list: (dealId, includeDismissed = false) =>
    fetch(`${API_BASE}/api/deals/${dealId}/matches?include_dismissed=${includeDismissed}`, { headers: getAuthHeaders() }).then(handleResponse),
  run: (dealId) =>
    fetch(`${API_BASE}/api/deals/${dealId}/matches/run`, { method: 'POST', headers: getAuthHeaders() }).then(handleResponse),
  update: (dealId, matchId, data) =>
    fetch(`${API_BASE}/api/deals/${dealId}/matches/${matchId}`, {
      method: 'PATCH', headers: getAuthHeaders(), body: JSON.stringify(data)
    }).then(handleResponse),
};

// Phase 89 — Lender Mandate Profile + Matching Score (internal-only).
// All endpoints 403 for lender / family_office / broker / borrower users.
export const lenderMandateAPI = {
  /** GET internal mandate profile + read-only investmentProfile context for a counterparty. */
  getMandate: (counterpartyId) =>
    fetch(`${API_BASE}/api/admin/counterparties/${counterpartyId}/mandate`, {
      headers: getAuthHeaders(),
    }).then(handleResponse),
  /** PUT partial mandate update. Body shape: subset of MandateProfileUpdate fields. */
  updateMandate: (counterpartyId, payload) =>
    fetch(`${API_BASE}/api/admin/counterparties/${counterpartyId}/mandate`, {
      method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(payload),
    }).then(handleResponse),
  /** GET per-deal scored lender matches grouped by tier. */
  getDealMatches: (dealId, includeExcluded = true) =>
    fetch(
      `${API_BASE}/api/admin/deals/${dealId}/lender-matches?include_excluded=${includeExcluded}`,
      { headers: getAuthHeaders() },
    ).then(handleResponse),
  /** GET per-list best-fit counts for the AdminDeals chip. */
  getDealsMatchesSummary: () =>
    fetch(`${API_BASE}/api/admin/deals/matches-summary`, {
      headers: getAuthHeaders(),
    }).then(handleResponse),
  /** GET canonical mandate taxonomy (statuses, dealStages, recoursePrefs). */
  getTaxonomy: () =>
    fetch(`${API_BASE}/api/admin/mandate/taxonomy`, {
      headers: getAuthHeaders(),
    }).then(handleResponse),
  /** Phase 89.1 — Bulk invite matched lenders. Pass `dryRun: true` to preview
   *  without writing; the response shape is the same in both modes. */
  bulkInvite: (dealId, payload) =>
    fetch(`${API_BASE}/api/admin/deals/${dealId}/bulk-invite-matches`, {
      method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(payload),
    }).then(handleResponse),
};

// Phase 90.1 — Send Procurement Pack (internal-only, external-safe links).
export const procurementAPI = {
  /** Send the public-safe trust/procurement pack to one or many recipients.
   *  Payload accepts {recipientEmails, counterpartyIds, dealId, customNote, dryRun}. */
  send: (payload) =>
    fetch(`${API_BASE}/api/admin/procurement-pack/send`, {
      method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(payload),
    }).then(handleResponse),
};

// Phase 91 — Advanced Q&A Governance (internal-only).
export const qaGovernanceAPI = {
  list: (dealId, params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return fetch(
      `${API_BASE}/api/admin/deals/${dealId}/questions/governance${qs ? `?${qs}` : ''}`,
      { headers: getAuthHeaders() },
    ).then(handleResponse);
  },
  saveDraft: (questionId, payload) =>
    fetch(`${API_BASE}/api/admin/questions/${questionId}/draft`, {
      method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(payload),
    }).then(handleResponse),
  approve: (questionId) =>
    fetch(`${API_BASE}/api/admin/questions/${questionId}/approve`, {
      method: 'PUT', headers: getAuthHeaders(),
    }).then(handleResponse),
  release: (questionId) =>
    fetch(`${API_BASE}/api/admin/questions/${questionId}/release`, {
      method: 'PUT', headers: getAuthHeaders(),
    }).then(handleResponse),
  assign: (questionId, assignedTo) =>
    fetch(`${API_BASE}/api/admin/questions/${questionId}/assign`, {
      method: 'PUT', headers: getAuthHeaders(),
      body: JSON.stringify({ assignedTo }),
    }).then(handleResponse),
  setCategory: (questionId, category) =>
    fetch(`${API_BASE}/api/admin/questions/${questionId}/category`, {
      method: 'PUT', headers: getAuthHeaders(),
      body: JSON.stringify({ category }),
    }).then(handleResponse),
  markDuplicate: (questionId, duplicateOfId) =>
    fetch(`${API_BASE}/api/admin/questions/${questionId}/mark-duplicate`, {
      method: 'PUT', headers: getAuthHeaders(),
      body: JSON.stringify({ duplicateOfId }),
    }).then(handleResponse),
  exportPack: (dealId, format = 'csv') =>
    fetch(`${API_BASE}/api/admin/deals/${dealId}/questions/export?format=${format}`, {
      headers: getAuthHeaders(),
    }).then(r => format === 'json' ? r.json() : r.blob()),
  /** Phase 91.1 — Drafts assigned to the current user (drafting + awaiting_review). */
  myDrafts: (includeApproved = false) =>
    fetch(`${API_BASE}/api/admin/qa/my-drafts?include_approved=${includeApproved}`, {
      headers: getAuthHeaders(),
    }).then(handleResponse),
  /** Phase 91.2 — Daily assigned-drafts digest status (cron config + recent lock entries). */
  digestStatus: () =>
    fetch(`${API_BASE}/api/admin/qa/digest/status`, {
      headers: getAuthHeaders(),
    }).then(handleResponse),
  /** Phase 91.2 — Manual trigger. `dryRun=true` previews recipients without sending. */
  digestRunNow: (dryRun = false) =>
    fetch(`${API_BASE}/api/admin/qa/digest/run-now?dry_run=${dryRun}`, {
      method: 'POST', headers: getAuthHeaders(),
    }).then(handleResponse),
};

// Phase 92 — Lender Response Analytics (internal-only).
export const lenderAnalyticsAPI = {
  /** Main internal analytics overview + breakdowns. */
  overview: (filters = {}) => {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(filters).filter(([_, v]) => v != null && v !== ''))
    ).toString();
    return fetch(
      `${API_BASE}/api/admin/analytics/lender-response${qs ? `?${qs}` : ''}`,
      { headers: getAuthHeaders() },
    ).then(handleResponse);
  },
  /** Aggregated CSV export — returns a Blob suitable for download. */
  exportCsv: (filters = {}) => {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(filters).filter(([_, v]) => v != null && v !== ''))
    ).toString();
    return fetch(
      `${API_BASE}/api/admin/analytics/lender-response/export.csv${qs ? `?${qs}` : ''}`,
      { headers: getAuthHeaders() },
    ).then(r => r.blob());
  },
  /** Phase 92.1 — Deal-level drilldown for a single cohort. */
  drilldown: ({ cohortType, cohortKey, ...filters }) => {
    const params = { cohortType, cohortKey, ...filters };
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([_, v]) => v != null && v !== ''))
    ).toString();
    return fetch(
      `${API_BASE}/api/admin/analytics/lender-response/drilldown?${qs}`,
      { headers: getAuthHeaders() },
    ).then(handleResponse);
  },
  /** Phase 92.2 — Weekly historical trend buckets for sparklines. */
  trend: (filters = {}) => {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(filters).filter(([_, v]) => v != null && v !== ''))
    ).toString();
    return fetch(
      `${API_BASE}/api/admin/analytics/lender-response/trend${qs ? `?${qs}` : ''}`,
      { headers: getAuthHeaders() },
    ).then(handleResponse);
  },
  /** Phase 92.3 — Engagement insight chip for a single deal. */
  dealEngagementInsight: (dealId) =>
    fetch(`${API_BASE}/api/admin/deals/${dealId}/engagement-insight`, {
      headers: getAuthHeaders(),
    }).then(handleResponse),
};

export const institutionLinkAPI = {
  link: (userId, counterpartyId) =>
    fetch(`${API_BASE}/api/users/${userId}/link-institution`, {
      method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ counterpartyId })
    }).then(handleResponse),
  unlink: (userId) =>
    fetch(`${API_BASE}/api/users/${userId}/link-institution`, {
      method: 'DELETE', headers: getAuthHeaders()
    }).then(handleResponse),
};

export const ncndaAPI = {
  getStatus: () =>
    fetch(`${API_BASE}/api/ncnda/status`, { headers: getAuthHeaders() }).then(handleResponse),
  accept: () =>
    fetch(`${API_BASE}/api/ncnda/accept`, {
      method: 'POST', headers: getAuthHeaders()
    }).then(handleResponse),
  getContent: () =>
    fetch(`${API_BASE}/api/ncnda/content`).then(handleResponse),
};

export const ncndaAdminAPI = {
  listVersions: () =>
    fetch(`${API_BASE}/api/ncnda/versions`, { headers: getAuthHeaders() }).then(handleResponse),
  getVersion: (id) =>
    fetch(`${API_BASE}/api/ncnda/versions/${id}`, { headers: getAuthHeaders() }).then(handleResponse),
  createVersion: (data) =>
    fetch(`${API_BASE}/api/ncnda/versions`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  updateVersion: (id, data) =>
    fetch(`${API_BASE}/api/ncnda/versions/${id}`, { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  publishVersion: (id) =>
    fetch(`${API_BASE}/api/ncnda/versions/${id}/publish`, { method: 'POST', headers: getAuthHeaders() }).then(handleResponse),
  listAcceptances: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return fetch(`${API_BASE}/api/ncnda/acceptances${qs ? '?' + qs : ''}`, { headers: getAuthHeaders() }).then(handleResponse);
  },
};

export const feeProtectionAPI = {
  list: (dealId) =>
    fetch(`${API_BASE}/api/deals/${dealId}/fee-protection`, { headers: getAuthHeaders() }).then(handleResponse),
  create: (dealId, data) =>
    fetch(`${API_BASE}/api/deals/${dealId}/fee-protection`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  update: (dealId, recordId, data) =>
    fetch(`${API_BASE}/api/deals/${dealId}/fee-protection/${recordId}`, { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  disclosePreExisting: (dealId, recordId, data) =>
    fetch(`${API_BASE}/api/deals/${dealId}/fee-protection/${recordId}/pre-existing`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data) }).then(handleResponse),
};

export const onboardingAPI = {
  status: () =>
    fetch(`${API_BASE}/api/onboarding/status`, { headers: getAuthHeaders() }).then(handleResponse),
  createOrganization: (data) =>
    fetch(`${API_BASE}/api/onboarding/create-organization`, {
      method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data)
    }).then(handleResponse),
  selectInstitution: (data) =>
    fetch(`${API_BASE}/api/onboarding/select-institution`, {
      method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data)
    }).then(handleResponse),
  skipInstitution: () =>
    fetch(`${API_BASE}/api/onboarding/skip-institution`, {
      method: 'POST', headers: getAuthHeaders()
    }).then(handleResponse),
  complete: () =>
    fetch(`${API_BASE}/api/onboarding/complete`, {
      method: 'POST', headers: getAuthHeaders()
    }).then(handleResponse),
};

export const permissionsAPI = {
  get: (userId) =>
    fetch(`${API_BASE}/api/users/${userId}/permissions`, { headers: getAuthHeaders() }).then(handleResponse),
  update: (userId, perms) =>
    fetch(`${API_BASE}/api/users/${userId}/permissions`, {
      method: 'PATCH', headers: getAuthHeaders(), body: JSON.stringify(perms)
    }).then(handleResponse),
};

export const twoFactorAPI = {
  setup: () =>
    fetch(`${API_BASE}/api/auth/2fa/setup`, { method: 'POST', headers: getAuthHeaders() }).then(handleResponse),
  verify: (code) =>
    fetch(`${API_BASE}/api/auth/2fa/verify-setup`, {
      method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ code })
    }).then(handleResponse),
};


export const termSheetsAPI = {
  list: (dealId) =>
    fetch(`${API_BASE}/api/deals/${dealId}/term-sheets`, { headers: getAuthHeaders() }).then(handleResponse),
  create: (dealId, formData) =>
    fetch(`${API_BASE}/api/deals/${dealId}/term-sheets`, {
      method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` }, body: formData
    }).then(handleResponse),
  resubmit: (dealId, tsId, formData) =>
    fetch(`${API_BASE}/api/deals/${dealId}/term-sheets/${tsId}/resubmit`, {
      method: 'PUT', headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` }, body: formData
    }).then(handleResponse),
  review: (dealId, tsId, data) =>
    fetch(`${API_BASE}/api/deals/${dealId}/term-sheets/${tsId}/review`, {
      method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data)
    }).then(handleResponse),
  download: (dealId, tsId) =>
    fetch(`${API_BASE}/api/deals/${dealId}/term-sheets/${tsId}/download`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` }
    }).then(r => { if (!r.ok) throw new Error('Download failed'); return r.blob(); }),
  setGpFee: (dealId, tsId, data) =>
    fetch(`${API_BASE}/api/deals/${dealId}/term-sheets/${tsId}/gp-fee`, {
      method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(data)
    }).then(handleResponse),
  setReferralCommission: (dealId, tsId, data) =>
    fetch(`${API_BASE}/api/deals/${dealId}/term-sheets/${tsId}/referral-commission`, {
      method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(data)
    }).then(handleResponse),
};

// Admin Compliance Tools API (Backup Status + Exports)
export const complianceAPI = {
  getBackupStatus: () =>
    fetch(`${API_BASE}/api/admin/backups/status`, { headers: getAuthHeaders() }).then(handleResponse),

  reportBackup: (data) =>
    fetch(`${API_BASE}/api/admin/backups/report`, {
      method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data)
    }).then(handleResponse),

  exportUsers: () =>
    fetch(`${API_BASE}/api/admin/export/users`, { headers: getAuthHeaders() })
      .then(r => { if (!r.ok) throw new Error('Export failed'); return r.blob(); }),

  exportDeals: () =>
    fetch(`${API_BASE}/api/admin/export/deals`, { headers: getAuthHeaders() })
      .then(r => { if (!r.ok) throw new Error('Export failed'); return r.blob(); }),

  exportLoginEvents: (days = 30) =>
    fetch(`${API_BASE}/api/admin/export/login-events?days=${days}`, { headers: getAuthHeaders() })
      .then(r => { if (!r.ok) throw new Error('Export failed'); return r.blob(); }),
};

export const anonymousQaAPI = {
  sendMessage: (dealId, data) =>
    fetch(`${API_BASE}/api/deals/${dealId}/anonymous-qa/messages`, {
      method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data)
    }).then(handleResponse),
};



export const incidentsAPI = {
  list: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return fetch(`${API_BASE}/api/admin/incidents${qs ? '?' + qs : ''}`, { headers: getAuthHeaders() }).then(handleResponse);
  },
  get: (id) => fetch(`${API_BASE}/api/admin/incidents/${id}`, { headers: getAuthHeaders() }).then(handleResponse),
  create: (data) => fetch(`${API_BASE}/api/admin/incidents`, { method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(data) }).then(handleResponse),
  update: (id, data) => fetch(`${API_BASE}/api/admin/incidents/${id}`, { method: 'PUT', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(data) }).then(handleResponse),
  addTimeline: (id, data) => fetch(`${API_BASE}/api/admin/incidents/${id}/timeline`, { method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(data) }).then(handleResponse),
  changeStatus: (id, status) => fetch(`${API_BASE}/api/admin/incidents/${id}/status`, { method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify({ status }) }).then(handleResponse),
};

export const auditRetentionAPI = {
  getSettings: () => fetch(`${API_BASE}/api/admin/audit-retention/settings`, { headers: getAuthHeaders() }).then(handleResponse),
  getStats: () => fetch(`${API_BASE}/api/admin/audit-retention/stats`, { headers: getAuthHeaders() }).then(handleResponse),
  updateSettings: (data) => fetch(`${API_BASE}/api/admin/audit-retention/settings`, { method: 'PUT', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(data) }).then(handleResponse),
  preview: () => fetch(`${API_BASE}/api/admin/audit-retention/preview`, { method: 'POST', headers: getAuthHeaders() }).then(handleResponse),
  cleanup: () => fetch(`${API_BASE}/api/admin/audit-retention/cleanup`, { method: 'POST', headers: getAuthHeaders() }).then(handleResponse),
};

export const lenderRequestsAPI = {
  list: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return fetch(`${API_BASE}/api/admin/lender-requests${query ? '?' + query : ''}`, { headers: getAuthHeaders() }).then(handleResponse);
  },
  updateStatus: (id, data) => fetch(`${API_BASE}/api/admin/lender-requests/${id}`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  }).then(handleResponse),
  grantAccessPreview: (id) => fetch(`${API_BASE}/api/admin/lender-requests/${id}/grant-access/preview`, {
    headers: getAuthHeaders(),
  }).then(handleResponse),
  grantAccess: async (id, body = {}) => {
    // Custom response handling: 409 returns a structured duplicates payload
    // that we must preserve (not flatten into an Error message).
    const res = await fetch(`${API_BASE}/api/admin/lender-requests/${id}/grant-access`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    });
    let data = null;
    try { data = await res.json(); } catch (_) {}
    if (res.ok) return data;
    if (res.status === 409 && data?.detail?.code === 'duplicate_detected') {
      const err = new Error(data.detail.message || 'Duplicates detected');
      err.code = 'duplicate_detected';
      err.duplicates = data.detail.duplicates || {};
      throw err;
    }
    throw new Error(data?.detail || 'Grant access failed');
  },
};


// ── Ops / Deploy-Confidence (Phase 60) ──

export const opsAPI = {
  buildInfo: () =>
    fetch(`${API_BASE}/api/__build_info`).then(handleResponse),

  listMigrations: () =>
    fetch(`${API_BASE}/api/admin/migrations`, { headers: getAuthHeaders() }).then(handleResponse),

  rolloutVerify: () =>
    fetch(`${API_BASE}/api/admin/rollout-verify`, { headers: getAuthHeaders() }).then(handleResponse),

  runNcndaMigration: () =>
    fetch(`${API_BASE}/api/admin/ncnda/run-migration`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: '{}',
    }).then(handleResponse),
};

// ── Selected Transactions CMS (Phase 61) ──

export const selectedTransactionsAPI = {
  listPublic: (limit = 24) =>
    fetch(`${API_BASE}/api/public/selected-transactions?limit=${limit}`).then(handleResponse),

  listAdmin: (status = '') =>
    fetch(`${API_BASE}/api/admin/selected-transactions${status ? `?status=${status}` : ''}`, {
      headers: getAuthHeaders(),
    }).then(handleResponse),

  // Phase 72 — Generate safe-only draft suggestion from an existing deal.
  // Returns { suggested: {...}, redactedFields: [...], source: {...} }.
  prefillFromDeal: (dealId) =>
    fetch(`${API_BASE}/api/admin/selected-transactions/prefill-from-deal/${dealId}`, {
      headers: getAuthHeaders(),
    }).then(handleResponse),

  create: (payload) =>
    fetch(`${API_BASE}/api/admin/selected-transactions`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    }).then(handleResponse),

  update: (id, patch) =>
    fetch(`${API_BASE}/api/admin/selected-transactions/${id}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(patch),
    }).then(handleResponse),

  delete: (id) =>
    fetch(`${API_BASE}/api/admin/selected-transactions/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    }).then(handleResponse),

  reorder: (order) =>
    fetch(`${API_BASE}/api/admin/selected-transactions/reorder`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ order }),
    }).then(handleResponse),
};

// Consumer enquiries remain separate from institutional lender access/mandates.
export const homeLoansAPI = {
  config: () => fetch('/api/public/home-loans/config').then(handleResponse),
  submit: payload => fetch('/api/public/home-loan-enquiries', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
  }).then(handleResponse),
  list: (page = 1) => fetch(`/api/admin/home-loan-enquiries?page=${page}`, { headers: getAuthHeaders() }).then(handleResponse),
};

// Separate staff sandbox: no institutional data queries or live CRM requests.
export const homeLoanWorkspaceAPI = {
  get: () => fetch('/api/home-loans/workspace', { headers: getAuthHeaders(), cache: 'no-store' }).then(handleResponse),
  enquiries: page => fetch(`/api/home-loans/workspace/enquiries?page=${page}`, { headers: getAuthHeaders(), cache: 'no-store' }).then(handleResponse),
  update: (id, payload) => fetch(`/api/home-loans/workspace/enquiries/${encodeURIComponent(id)}`, {
    method: 'PATCH', headers: getAuthHeaders(), cache: 'no-store', body: JSON.stringify(payload),
  }).then(handleResponse),
  preview: payload => fetch('/api/home-loans/workspace/handoff-preview', {
    method: 'POST', headers: getAuthHeaders(), cache: 'no-store', body: JSON.stringify(payload),
  }).then(handleResponse),
};
